(function () {
  if (window.__sizeFinderInit) return;
  window.__sizeFinderInit = true;

  var STORAGE_KEY = 'sizeFinderProfile';
  var PROFILE_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

  // -----------------------------------------------------------------------
  // Recommendation logic — ported verbatim from size-logic.js (project
  // root, v3: per-product charts). Two-tier engine: Tier 1 estimates/
  // measures chest circumference and matches it against each size's
  // garment chest spec + ease; Tier 2 (used only when a chart has no
  // garmentChest values — including every DOM-parsed chart, see below)
  // falls back to height/weight range scoring. Do NOT change the math
  // here — only re-run `node size-logic.js` (13 cases) and
  // `node size-guide-parser.js` (17 cases) to re-verify if this section
  // or the parser section below is ever touched.
  // -----------------------------------------------------------------------

  var CONFIG = {
    // chest = base + wCoef*kg + hCoef*cm (then + buildOffset)
    chestModel: {
      men: { base: 34.0, wCoef: 0.52, hCoef: 0.122 },
      women: { base: 30.0, wCoef: 0.55, hCoef: 0.141 }
    },
    buildOffset: { slim: -3, athletic: 0, broad: 5 },
    // Ease = garmentChest - userChest (cm). Negative = stretched/fitted.
    ease: { easeMin: -7, easeMax: 8 },
    preferenceTargetEase: { tight: -6, regular: -3, relaxed: 0 },
    tier2Weights: { weight: 0.7, height: 0.3 },
    tier2BuildShift: { slim: 0, athletic: 0, broad: 1 }
  };

  // ---------------------------------------------------------------------
  // CHARTS (height/weight from the live site; garmentChest are calibrated
  // estimates). The store uses THREE different charts per KEY FINDING in
  // size-logic.js: women's apparel shares one chart, but men's Tank runs
  // roomier than men's Tee/Shorts/Tights (same man = M in Tank, L in Tee)
  // — per-product mapping below is mandatory, never one men's chart for
  // everything.
  // ---------------------------------------------------------------------
  var CHARTS = {
    WOMEN_STANDARD: {
      sex: 'women',
      sizes: [
        { size: 'XS', height: [150, 175], weight: [40, 50], garmentChest: 76 },
        { size: 'S', height: [155, 178], weight: [50, 59], garmentChest: 81 },
        { size: 'M', height: [160, 180], weight: [59, 68], garmentChest: 86 },
        { size: 'L', height: [165, 183], weight: [68, 80], garmentChest: 92 },
        { size: 'XL', height: [170, 185], weight: [80, 92], garmentChest: 99 }
      ]
    },
    MEN_TANK: {
      sex: 'men',
      sizes: [
        { size: 'XS', height: [165, 172], weight: [55, 68], garmentChest: 86 },
        { size: 'S', height: [168, 178], weight: [60, 75], garmentChest: 91 },
        { size: 'M', height: [175, 186], weight: [72, 88], garmentChest: 97 },
        { size: 'L', height: [180, 192], weight: [85, 95], garmentChest: 103 },
        { size: 'XL', height: [183, 198], weight: [95, 105], garmentChest: 109 },
        { size: 'XXL', height: [188, 205], weight: [105, 130], garmentChest: 115 }
      ]
    },
    MEN_SLIM: {
      sex: 'men',
      sizes: [
        { size: 'XS', height: [160, 165], weight: [50, 55], garmentChest: 81 },
        { size: 'S', height: [165, 172], weight: [55, 63], garmentChest: 86 },
        { size: 'M', height: [170, 178], weight: [63, 72], garmentChest: 91 },
        { size: 'L', height: [175, 183], weight: [72, 80], garmentChest: 96 },
        { size: 'XL', height: [180, 195], weight: [80, 92], garmentChest: 102 },
        { size: 'XXL', height: [185, 200], weight: [92, 110], garmentChest: 108 }
      ]
    }
  };

  // Product handle -> chart. Color variants of the same garment share a chart.
  var PRODUCT_CHART_MAP = {
    'womens-velocity-cropped-tank-charcoal-black': 'WOMEN_STANDARD',
    'womens-velocity-cropped-top-charcoal-black-lunar-white-flash-yellow': 'WOMEN_STANDARD',
    'women-s-velocity-aquarun-suit': 'WOMEN_STANDARD',
    'womens-velocity-sprint-romper-charcoal-black': 'WOMEN_STANDARD',
    'womens-velocity-flash-romper-flash-yellow': 'WOMEN_STANDARD',
    'womens-velocity-power-bra-charcoal-black': 'WOMEN_STANDARD',
    'mens-velocity-tank-charcoal-black': 'MEN_TANK',
    'mens-velocity-tank-flash-yellow': 'MEN_TANK',
    'mens-velocity-fitted-tee-charcoal-black': 'MEN_SLIM',
    'mens-velocity-flow-shorts-charcoal-black': 'MEN_SLIM',
    'mens-velocity-tights-charcoal-black': 'MEN_SLIM'
  };

  /** Resolve chart: explicit handle map -> handle prefix -> sex default. */
  function resolveChart(handle, sex) {
    if (handle && PRODUCT_CHART_MAP[handle]) return CHARTS[PRODUCT_CHART_MAP[handle]];
    if (handle && /^women|^womens/.test(handle)) return CHARTS.WOMEN_STANDARD;
    if (handle && /^mens?-/.test(handle)) return CHARTS.MEN_SLIM; // slim = majority of men's line
    return sex === 'women' ? CHARTS.WOMEN_STANDARD : CHARTS.MEN_SLIM;
  }

  /**
   * recommendSize(user, productHandle?, explicitChart?)
   * user: { sex: 'men'|'women', height: cm, weight: kg,
   *         build: 'slim'|'athletic'|'broad', preference: 'tight'|'regular'|'relaxed',
   *         chest?: cm, underbust?: cm }
   * explicitChart: optional pre-resolved array of size rows — used to feed
   *   in a DOM-parsed or metafield-override chart, bypassing resolveChart()
   *   entirely (see getEffectiveChart() below).
   * returns { size, estimatedChest, method }
   */
  function recommendSize(user, productHandle, explicitChart) {
    var c = explicitChart || resolveChart(productHandle, user.sex).sizes;
    var build = user.build || 'athletic';
    var pref = user.preference || 'regular';

    var hasGarmentSpecs = c.every(function (row) { return typeof row.garmentChest === 'number'; });
    var result = hasGarmentSpecs
      ? tier1GarmentMatch(user, c, build, pref)
      : tier2RangeScore(user, c, build, pref);

    return result;
  }

  // --- TIER 1: chest estimation + garment matching --------------------------
  function estimateChest(user, build) {
    // MEASURED beats ESTIMATED: if the user entered a real chest measurement,
    // use it directly — no build offset (the tape measure already "knows" the build).
    if (typeof user.chest === 'number' && user.chest > 0) {
      // For women, if underbust (ribcage) is also given, blend it in: the tank
      // must fit both the fullest point and the ribcage. Weighted toward chest.
      if (user.sex === 'women' && typeof user.underbust === 'number' && user.underbust > 0) {
        return 0.75 * user.chest + 0.25 * (user.underbust + 10);
      }
      return user.chest;
    }
    var m = CONFIG.chestModel[user.sex];
    return m.base + m.wCoef * user.weight + m.hCoef * user.height + CONFIG.buildOffset[build];
  }

  function tier1GarmentMatch(user, chart, build, pref) {
    var chest = estimateChest(user, build);
    var targetEase = CONFIG.preferenceTargetEase[pref];
    var easeMin = CONFIG.ease.easeMin;
    var easeMax = CONFIG.ease.easeMax;

    // Score every size by how close its actual ease (garmentChest - chest)
    // is to the preferred ease. Sizes whose ease falls outside the wearable
    // window [easeMin, easeMax] get a heavy penalty (still rankable => nearest).
    var best = null;
    for (var i = 0; i < chart.length; i++) {
      var row = chart[i];
      var easeVal = row.garmentChest - chest;
      var score = Math.abs(easeVal - targetEase);
      if (easeVal < easeMin) score += 100 + (easeMin - easeVal); // unwearably tight
      if (easeVal > easeMax) score += 50 + (easeVal - easeMax); // clearly too big
      if (!best || score < best.score) best = { row: row, score: score, easeVal: easeVal };
    }

    return {
      size: best.row.size,
      estimatedChest: Math.round(chest * 10) / 10,
      method: 'garment-match'
    };
  }

  // --- TIER 2: height/weight range scoring (fallback) -----------------------
  function tier2RangeScore(user, chart, build, pref) {
    var w = CONFIG.tier2Weights;
    var bestIdx = 0;
    var bestScore = Infinity;
    chart.forEach(function (row, i) {
      var wMid = (row.weight[0] + row.weight[1]) / 2;
      var hMid = (row.height[0] + row.height[1]) / 2;
      var wSpan = (row.weight[1] - row.weight[0]) / 2 || 1;
      var hSpan = (row.height[1] - row.height[0]) / 2 || 1;
      var score =
        (w.weight * Math.abs(user.weight - wMid)) / wSpan +
        (w.height * Math.abs(user.height - hMid)) / hSpan;
      if (score < bestScore) {
        bestScore = score;
        bestIdx = i;
      }
    });

    // Build shift (broad = +1)
    bestIdx = Math.min(chart.length - 1, bestIdx + CONFIG.tier2BuildShift[build]);

    // Fit preference shift
    var row = chart[bestIdx];
    var wMid = (row.weight[0] + row.weight[1]) / 2;
    if (pref === 'tight' && user.weight < wMid && bestIdx > 0) bestIdx -= 1;
    if (pref === 'relaxed' && bestIdx < chart.length - 1) bestIdx += 1;

    return {
      size: chart[bestIdx].size,
      estimatedChest: null,
      method: 'range-score'
    };
  }

  // -----------------------------------------------------------------------
  // Availability check (post-recommendation) — ported verbatim from
  // size-logic.js (run `node size-logic.js` to re-verify its 15 cases,
  // including the 2 availability-specific ones). recommendSize() above
  // NEVER knows about stock/offerings — it always returns the customer's
  // TRUE size. Whether that size can actually be bought is answered here,
  // as a separate step, so the true size is never silently swapped for a
  // substitute and presented as "the recommendation."
  // -----------------------------------------------------------------------

  function checkSizeAvailability(idealSize, chartSizes, offeredSizes, inStockSizes) {
    var order = chartSizes.map(function (e) { return e.size; });
    var offered = (offeredSizes || []).map(function (s) { return String(s).trim().toUpperCase(); });
    var inStock = (inStockSizes || []).map(function (s) { return String(s).trim().toUpperCase(); });

    var isOffered = offered.indexOf(idealSize) !== -1;
    var isInStock = inStock.indexOf(idealSize) !== -1;

    if (isOffered && isInStock) return { status: 'available', size: idealSize };
    if (isOffered && !isInStock) return { status: 'sold-out', size: idealSize };

    // Not offered at all: find the nearest offered size BELOW and ABOVE
    // separately, rather than a single "nearest" with implicit tie-breaking.
    // A catalog can have gaps (e.g. only S and L offered, M skipped) — in
    // that case there's no single honest "boundary," so collapsing it to
    // one direction would produce a false "only available up to X" claim
    // while an offered size also sits just past it the other way.
    var idealIdx = order.indexOf(idealSize);
    if (idealIdx === -1 || offered.length === 0) {
      return { status: 'not-offered', size: idealSize, boundarySize: null, gap: null, direction: null };
    }

    var belowGap = Infinity;
    var aboveGap = Infinity;
    order.forEach(function (s, i) {
      if (offered.indexOf(s) === -1) return;
      if (i < idealIdx) belowGap = Math.min(belowGap, idealIdx - i);
      if (i > idealIdx) aboveGap = Math.min(aboveGap, i - idealIdx);
    });

    if (belowGap === Infinity && aboveGap === Infinity) {
      return { status: 'not-offered', size: idealSize, boundarySize: null, gap: null, direction: null };
    }

    // Sandwiched: an offered size sits one step away on BOTH sides. Neither
    // is "the" boundary — same honest fallback as the gap>=2 case below (the
    // alt-sizes row already lists every real option).
    if (belowGap === 1 && aboveGap === 1) {
      return { status: 'not-offered', size: idealSize, boundarySize: null, gap: 1, direction: null };
    }

    var nearestGap = Math.min(belowGap, aboveGap);
    var direction = belowGap <= aboveGap ? 'down' : 'up'; // down = offered size is SMALLER than ideal
    var nearestIdx = direction === 'down' ? idealIdx - belowGap : idealIdx + aboveGap;

    if (nearestGap === 1) {
      return { status: 'not-offered', size: idealSize, boundarySize: order[nearestIdx], gap: nearestGap, direction: direction };
    }

    return { status: 'not-offered', size: idealSize, boundarySize: null, gap: nearestGap, direction: direction };
  }

  // Turns a checkSizeAvailability() result into the exact customer-facing
  // copy. Returns null for "available". Direction comes straight from
  // chart order (a smaller size on a given body is always tighter), so
  // this is never copied from the true size's own fit note.
  function availabilityMessage(avail) {
    if (avail.status === 'available') return null;

    if (avail.status === 'sold-out') {
      return { message: 'Your size is ' + avail.size + ' — currently out of stock.', suggestion: null };
    }

    if (avail.boundarySize && avail.gap === 1) {
      var boundaryPhrase = avail.direction === 'down'
        ? 'This product is only available up to ' + avail.boundarySize + '.'
        : 'This product is only available from ' + avail.boundarySize + '.';
      var fitPhrase = avail.direction === 'down'
        ? avail.boundarySize + ' will fit noticeably tighter than intended.'
        : avail.boundarySize + ' will fit noticeably looser than intended.';
      return { message: 'Your size is ' + avail.size + '. ' + boundaryPhrase, suggestion: fitPhrase };
    }

    return { message: 'Your size is ' + avail.size + ". This product doesn't come in your size.", suggestion: null };
  }

  // -----------------------------------------------------------------------
  // Size guide auto-parser — ported from size-guide-parser.js (project
  // root; run `node size-guide-parser.js` to re-verify its 17 cases).
  // Split the same way as that file: parseTableToChart() is a pure data
  // transform (no DOM); everything below it is the DOM-facing half that
  // only exists here, since size-guide-parser.js has no browser to test
  // against.
  // -----------------------------------------------------------------------

  var OPEN_RANGE_BUFFER = 25; // "105+" -> [105, 105 + 25] = [105, 130]

  function parseMeasurementRange(text) {
    if (text === undefined || text === null) return null;
    var s = String(text).trim();

    // Hyphen-minus "150-175", en dash "165–172", em dash, all via one class.
    var rangeMatch = s.match(/(\d+(?:\.\d+)?)\s*[-–—]\s*(\d+(?:\.\d+)?)/);
    if (rangeMatch) return [parseFloat(rangeMatch[1]), parseFloat(rangeMatch[2])];

    var openMatch = s.match(/(\d+(?:\.\d+)?)\s*\+/);
    if (openMatch) {
      var min = parseFloat(openMatch[1]);
      return [min, min + OPEN_RANGE_BUFFER];
    }

    var singleMatch = s.match(/(\d+(?:\.\d+)?)/);
    if (singleMatch) {
      var v = parseFloat(singleMatch[1]);
      return [v, v];
    }

    return null;
  }

  function parseSingleNumber(text) {
    if (text === undefined || text === null) return null;
    var m = String(text).match(/(\d+(?:\.\d+)?)/);
    return m ? parseFloat(m[1]) : null;
  }

  // Fallback for size-guide tables that aren't the height/weight garment-fit
  // shape (e.g. compression items' EU calf/foot range: "Size" + "EU"). NOT
  // usable by recommend()'s height/weight matching — this deliberately
  // returns a plain object rather than an array, so getEffectiveChart's
  // Array.isArray() check skips it for that path and the Virtual Fitting
  // Room quiz keeps using the generic per-sex chart, same as before this
  // existed. Only renderChartTableRows (display) reads this shape.
  function parseSimpleTableToChart(rows, header, sizeCol) {
    var valueCol = sizeCol === 0 ? 1 : 0;
    if (!header[valueCol]) return null;
    var label = String(header[valueCol]).trim();
    if (!label) return null;

    var entries = [];
    for (var r = 1; r < rows.length; r++) {
      var row = rows[r];
      if (!row || row.length === 0) continue;
      var sizeText = String(row[sizeCol] || '').trim();
      var valueText = String(row[valueCol] || '').trim();
      if (!sizeText || !valueText) continue;
      entries.push({ size: sizeText, value: valueText });
    }

    if (entries.length < 2) return null;
    return { simple: true, label: label, rows: entries };
  }

  // rows: array of arrays of cell text, first row = header. Returns an
  // array of { size, height, weight, garmentChest? } or null if it can't
  // be confidently parsed — callers must fall back on null.
  function parseTableToChart(rows) {
    if (!Array.isArray(rows) || rows.length < 2) return null;

    var header = rows[0];
    var sizeCol = -1;
    var heightCol = -1;
    var weightCol = -1;
    var chestCol = -1;

    header.forEach(function (cell, i) {
      var t = String(cell || '').trim().toLowerCase();
      if (sizeCol === -1 && t.indexOf('size') !== -1) sizeCol = i;
      if (heightCol === -1 && t.indexOf('height') !== -1) heightCol = i;
      if (weightCol === -1 && t.indexOf('weight') !== -1) weightCol = i;
      if (chestCol === -1 && (t.indexOf('chest') !== -1 || t.indexOf('bust') !== -1)) chestCol = i;
    });

    if (sizeCol === -1) sizeCol = 0;
    if (heightCol === -1 || weightCol === -1) return parseSimpleTableToChart(rows, header, sizeCol);

    var sizes = [];
    for (var r = 1; r < rows.length; r++) {
      var row = rows[r];
      if (!row || row.length === 0) continue;

      var sizeText = String(row[sizeCol] || '').trim();
      if (!sizeText) continue;

      var height = parseMeasurementRange(row[heightCol]);
      var weight = parseMeasurementRange(row[weightCol]);
      if (!height || !weight) continue;

      var entry = { size: sizeText, height: height, weight: weight };
      if (chestCol !== -1) {
        var chestNum = parseSingleNumber(row[chestCol]);
        if (chestNum !== null) entry.garmentChest = chestNum;
      }
      sizes.push(entry);
    }

    if (sizes.length < 3) return null;

    // All-or-nothing: a partially-filled chest column is worse than none.
    if (chestCol !== -1 && !sizes.every(function (s) { return typeof s.garmentChest === 'number'; })) {
      sizes.forEach(function (s) { delete s.garmentChest; });
    }

    return sizes;
  }

  // Finds the "Size guide" accordion regardless of collapsible-vs-static
  // block style, by its visible label text rather than a generated block
  // ID (so it works for any current or future product).
  function findSizeGuideContentElement() {
    var candidates = document.querySelectorAll('accordeon-button, .product-content-tab__title');
    for (var i = 0; i < candidates.length; i++) {
      var el = candidates[i];
      if (!/size\s*guide/i.test(el.textContent)) continue;
      var wrapper = el.closest('accordeon-component, .product-content-tab');
      if (!wrapper) continue;
      var content = wrapper.querySelector('[data-accordeon-content], .product-content-tab__content');
      if (content) return content;
    }
    return null;
  }

  // The store's size guide content has a real content bug: raw <table>
  // HTML was typed into a Shopify rich-text metafield, which escapes
  // angle brackets into literal visible text ("&lt;table&gt;") instead of
  // real markup. A separate script already on this theme fixes this on
  // DOMContentLoaded, but our own script is `defer`red and can run BEFORE
  // that fires — so we can't rely on it and do the same recovery
  // ourselves: textContent decodes the entities back into literal
  // "<table>" characters, and re-parsing that string as innerHTML makes
  // the browser build a real table from it.
  function findRealOrRecoveredTable(contentEl) {
    var table = contentEl.querySelector('table');
    if (table) return table;

    var rawText = contentEl.textContent;
    if (rawText.indexOf('<table') === -1 || rawText.indexOf('</table>') === -1) return null;

    var scratch = document.createElement('div');
    scratch.innerHTML = rawText;
    return scratch.querySelector('table');
  }

  function extractRowsFromTable(tableEl) {
    var trs = tableEl.querySelectorAll('tr');
    var rows = [];
    for (var i = 0; i < trs.length; i++) {
      var cells = trs[i].querySelectorAll('td, th');
      var row = [];
      for (var j = 0; j < cells.length; j++) row.push(cells[j].textContent.trim());
      rows.push(row);
    }
    return rows;
  }

  // Tier 0: this product's own published size guide, parsed once per page
  // load. Returns a chart array or null (malformed/missing/too few rows —
  // callers fall back to the existing PRODUCT_CHART_MAP/sex chain, never
  // throw). Wrapped in try/catch as a last line of defense: a parsing bug
  // must never break the rest of the widget.
  function parseSizeGuideFromDOM() {
    try {
      var contentEl = findSizeGuideContentElement();
      if (!contentEl) return null;
      var table = findRealOrRecoveredTable(contentEl);
      if (!table) return null;
      return parseTableToChart(extractRowsFromTable(table));
    } catch (e) {
      return null;
    }
  }

  // Priority order: parsed DOM table (Tier 0) -> metafield override ->
  // PRODUCT_CHART_MAP by handle -> fallback by sex. A parsed/override
  // chart is used outright regardless of declared sex — it's THIS
  // product's own published guide, which is more authoritative than a
  // generic per-sex default.
  function getEffectiveChart(productHandle, sex, parsedChart, metafieldOverride) {
    if (Array.isArray(parsedChart) && parsedChart.length >= 3) return parsedChart;
    if (Array.isArray(metafieldOverride) && metafieldOverride.length >= 3) return metafieldOverride;
    return resolveChart(productHandle, sex).sizes;
  }

  // Wrapper that builds the user object recommendSize() expects, resolves
  // the effective chart ONCE, and always returns the TRUE ideal size —
  // availability is reported alongside it, never substituted into it.
  function recommend(profile, offeredSizes, inStockSizes, productHandle, parsedChart, metafieldOverride) {
    var user = {
      sex: profile.gender,
      height: profile.height,
      weight: profile.weight,
      build: profile.build,
      preference: profile.fit,
      chest: profile.chest,
      underbust: profile.underbust
    };

    var chartSizes = getEffectiveChart(productHandle, profile.gender, parsedChart, metafieldOverride);
    var result = recommendSize(user, productHandle, chartSizes);
    var availability = checkSizeAvailability(result.size, chartSizes, offeredSizes, inStockSizes);

    return {
      idealSize: result.size,
      fit: profile.fit,
      estimatedChest: result.estimatedChest,
      method: result.method,
      chartSizes: chartSizes,
      availability: availability
    };
  }

  window.SizeFinderLogic = {
    CONFIG: CONFIG,
    CHARTS: CHARTS,
    PRODUCT_CHART_MAP: PRODUCT_CHART_MAP,
    resolveChart: resolveChart,
    recommendSize: recommendSize,
    estimateChest: estimateChest,
    checkSizeAvailability: checkSizeAvailability,
    availabilityMessage: availabilityMessage,
    parseTableToChart: parseTableToChart,
    getEffectiveChart: getEffectiveChart,
    recommend: recommend
  };

  // -----------------------------------------------------------------------
  // Presentation helpers
  // -----------------------------------------------------------------------

  function orderedAvailableSizes(availableSizes, chartSizes) {
    var order = chartSizes.map(function (e) { return e.size; });
    var normalizedAvailable = (availableSizes || []).map(function (s) {
      return String(s).trim().toUpperCase();
    });
    if (!normalizedAvailable.length) return order;
    return order.filter(function (s) { return normalizedAvailable.indexOf(s) !== -1; });
  }

  // -----------------------------------------------------------------------
  // Persistence
  // -----------------------------------------------------------------------

  // A saved profile is the customer's FINAL answer: once it exists (and
  // hasn't expired), the form never reopens and nothing here lets it be
  // edited — only clearing browser storage or the 30-day TTL resets it.
  function loadProfile() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var profile = JSON.parse(raw);

      var isValidShape =
        profile &&
        (profile.gender === 'women' || profile.gender === 'men') &&
        typeof profile.height === 'number' &&
        typeof profile.weight === 'number' &&
        (profile.fit === 'tight' || profile.fit === 'regular' || profile.fit === 'relaxed') &&
        typeof profile.savedAt === 'number';

      if (!isValidShape) return null;

      var isExpired = Date.now() - profile.savedAt > PROFILE_TTL_MS;
      if (isExpired) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }

      return profile;
    } catch (e) {
      /* ignore corrupt storage */
    }
    return null;
  }

  function saveProfile(profile, recommendedSize) {
    try {
      var record = {
        gender: profile.gender,
        height: profile.height,
        weight: profile.weight,
        fit: profile.fit,
        build: profile.build,
        chest: profile.chest,
        underbust: profile.underbust,
        recommendedSize: recommendedSize,
        savedAt: Date.now()
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    } catch (e) {
      /* storage unavailable, degrade silently */
    }
  }

  // Distinct log of "we recommended a size the customer couldn't actually
  // buy" events — separate from the profile record above, kept small and
  // capped so it can't grow unbounded. This is the data that answers
  // "which sizes are we losing sales on": e.g. "recommended XL, unavailable,
  // product max L". Deliberately localStorage-only (not a live cart-
  // attribute write) — see summary notes on why.
  var AVAILABILITY_LOG_KEY = 'sizeFinderAvailabilityEvents';
  var AVAILABILITY_LOG_MAX = 25;

  function availabilityEventSummary(productHandle, avail) {
    if (avail.status === 'sold-out') {
      return 'recommended ' + avail.size + ', sold out, product ' + productHandle;
    }
    if (avail.boundarySize) {
      return 'recommended ' + avail.size + ', unavailable, product max ' + avail.boundarySize;
    }
    return 'recommended ' + avail.size + ', unavailable, not offered at all, product ' + productHandle;
  }

  function logAvailabilityEvent(productHandle, avail) {
    if (!avail || avail.status === 'available') return null;
    var summary = availabilityEventSummary(productHandle, avail);

    try {
      var raw = localStorage.getItem(AVAILABILITY_LOG_KEY);
      var log = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(log)) log = [];
      log.push({
        at: Date.now(),
        productHandle: productHandle,
        status: avail.status,
        idealSize: avail.size,
        boundarySize: avail.boundarySize || null,
        summary: summary
      });
      if (log.length > AVAILABILITY_LOG_MAX) log = log.slice(log.length - AVAILABILITY_LOG_MAX);
      localStorage.setItem(AVAILABILITY_LOG_KEY, JSON.stringify(log));
    } catch (e) {
      /* storage unavailable, degrade silently */
    }

    return summary;
  }

  // -----------------------------------------------------------------------
  // Variant selection on the real product form
  // -----------------------------------------------------------------------

  function selectVariantSize(sectionId, optionName, optionPosition, sizeValue) {
    if (!sectionId || !optionName || !optionPosition) return;
    var picker = document.getElementById('product-variant-picker-' + sectionId);
    if (!picker) return;

    var radioName = optionName + '-' + optionPosition;
    var inputs = picker.querySelectorAll('input[type="radio"]');

    for (var i = 0; i < inputs.length; i++) {
      if (inputs[i].name === radioName && inputs[i].value === sizeValue) {
        inputs[i].checked = true;
        inputs[i].dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
    }
  }

  // -----------------------------------------------------------------------
  // Modal wiring
  //
  // The modal (this function) lives OUTSIDE the theme's <variant-picker>
  // custom element, so it is never destroyed by the AJAX re-render that
  // element performs on a real 'change' event (which selectVariantSize()
  // above deliberately triggers). The trigger LINKS, however, still live
  // INSIDE <variant-picker> (so they render next to the "Size" label) and
  // therefore CAN be replaced by that same re-render. So trigger clicks are
  // handled via a single document-level delegated listener instead of a
  // direct binding — that way they keep working even if the theme swaps
  // the trigger nodes out from under us. Each modal instance exposes an
  // API object on its own root node; the delegated handler looks it up by
  // matching data-instance-id between the (possibly-replaced) trigger
  // group and the (stable) modal root.
  // -----------------------------------------------------------------------

  function initModal(root) {
    // Defense in depth: the modal shouldn't be inside <variant-picker>
    // anymore, but keep this so our own form fields never leak change/input
    // events into some other ancestor listener.
    root.addEventListener('change', function (e) { e.stopPropagation(); });
    root.addEventListener('input', function (e) { e.stopPropagation(); });

    // Keyboard avoidance. Two problems, two fixes:
    //
    // 1) The modal's max-height (85vh) is relative to the LAYOUT viewport
    //    (window.innerHeight), which on iOS typically does NOT shrink when
    //    the keyboard opens — only the VISUAL viewport does. So the modal
    //    can stay taller than the space actually left on screen, with no
    //    way for its internal scroll region to help (nothing overflows a
    //    box that's allowed to be taller than the whole visible area).
    //    Fix: while the keyboard is open, cap the modal's height to the
    //    real visual viewport via an inline style, so it visually fits and
    //    .size-finder__body-scroll gets real overflow to work with.
    //
    // 2) Even inside a now-genuinely-scrollable region, the focused field
    //    isn't automatically scrolled into the visible part of it. Plain
    //    scrollIntoView() has the same layout-vs-visual-viewport blind spot
    //    as (1), so this reads window.visualViewport directly and adjusts
    //    the known scroll container's scrollTop by hand instead.
    //
    // Shrinking the modal alone isn't enough either: the overlay that
    // centers it (align-items: center) still spans the full, un-shrunk
    // layout viewport, so a shorter modal just ends up centered lower down
    // — including possibly below the visible area. The overlay's own
    // height has to track the visual viewport too so centering happens
    // within the space that's actually on screen.
    function shrinkModalForKeyboard() {
      if (!window.visualViewport || overlay.hidden) return;
      var vv = window.visualViewport;
      var keyboardLikelyOpen = vv.height < window.innerHeight - 80;
      if (keyboardLikelyOpen) {
        overlay.style.height = vv.height + 'px';
        overlay.style.top = vv.offsetTop + 'px';
        fittingModalEl.style.maxHeight = Math.max(200, vv.height - 32) + 'px';
      } else {
        overlay.style.height = '';
        overlay.style.top = '';
        fittingModalEl.style.maxHeight = '';
      }
    }

    function scrollFocusedInputAboveKeyboard(el) {
      if (!el) return;
      var scrollContainer = el.closest('.size-finder__body-scroll');
      if (!window.visualViewport) {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        return;
      }
      var rect = el.getBoundingClientRect();
      var vv = window.visualViewport;
      var visibleTop = vv.offsetTop;
      var visibleBottom = vv.offsetTop + vv.height;
      var margin = 16;
      if (rect.bottom > visibleBottom) {
        if (scrollContainer) scrollContainer.scrollTop += (rect.bottom - visibleBottom) + margin;
        else el.scrollIntoView({ block: 'end', behavior: 'smooth' });
      } else if (rect.top < visibleTop) {
        if (scrollContainer) scrollContainer.scrollTop -= (visibleTop - rect.top) + margin;
        else el.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
    }

    root.addEventListener('focusin', function (e) {
      var el = e.target;
      if (!el || el.tagName !== 'INPUT') return;
      // Delay lets Safari's keyboard-open animation get underway first;
      // the visualViewport 'resize' listener below re-checks once it
      // actually finishes, so this timeout just handles the common case
      // without waiting on that event.
      setTimeout(function () {
        shrinkModalForKeyboard();
        scrollFocusedInputAboveKeyboard(el);
      }, 300);
    });

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', function () {
        shrinkModalForKeyboard();
        var el = document.activeElement;
        if (el && el.tagName === 'INPUT' && root.contains(el)) {
          scrollFocusedInputAboveKeyboard(el);
        }
      });
    }

    var configEl = root.querySelector('[data-size-finder-config]');
    var config = {
      availableSizes: [],
      inStockSizes: [],
      chartOverride: null,
      productHandle: '',
      sectionId: '',
      optionName: '',
      optionPosition: ''
    };
    if (configEl) {
      try {
        config = JSON.parse(configEl.textContent);
      } catch (e) {
        /* keep defaults */
      }
    }

    var availableSizes = config.availableSizes || [];
    // Sizes offered as an OPTION at all vs. sizes with actual purchasable
    // stock right now — these can differ, and that gap is exactly what
    // distinguishes "not offered" from "sold out" below. Config is always
    // rendered fresh from the current page (never cached across deploys),
    // so an empty array here is a real "nothing in stock," not stale data.
    var inStockSizes = config.inStockSizes || [];
    // Parsed ONCE per page load, reused for both the recommendation and
    // the Size Chart modal (requirement: "parse once, use twice").
    // config.parsedChart: a product's own guide table handed in directly by
    // nt-bundle-builder.js (Race Kit bundle pages have no size-guide
    // accordion to parse). Product pages never set it, so they still use
    // their own DOM guide exactly as before.
    var parsedChart = (Array.isArray(config.parsedChart) && config.parsedChart.length >= 3) ? config.parsedChart : parseSizeGuideFromDOM();
    var lastLoggedAvailabilitySummary = null;

    // -----------------------------------------------------------------------
    // Cart attribute sync — a merchandising-facing companion to the
    // localStorage event log above. Written to the SAME cart every time the
    // recommendation changes or the customer actually adds a (possibly
    // different) size to cart, so "which sizes are we losing sales on" is
    // visible per-order in Shopify admin, not just in the browser.
    //
    // Verified empirically against this store's /cart/update.js: sending
    // { attributes: { "_sizeFinder": ... } } merges by key and leaves any
    // other app's cart attributes untouched (no read-modify-write needed).
    // -----------------------------------------------------------------------
    var CART_ATTR_KEY = '_sizeFinder';
    var lastWrittenCartAttrValue = null;
    var lastAddedSize = null;

    function writeSizeFinderCartAttribute(value) {
      if (!value || value === lastWrittenCartAttrValue) return;
      lastWrittenCartAttrValue = value;
      var attributes = {};
      attributes[CART_ATTR_KEY] = value;
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: attributes })
      }).catch(function () {
        // Best-effort analytics only — never let this interrupt shopping.
        lastWrittenCartAttrValue = null;
      });
    }

    function buildCartAttrValue(recSize, availStatus, boughtSize, build) {
      var followed = (boughtSize && recSize && String(boughtSize).toUpperCase() === String(recSize).toUpperCase())
        ? 'yes'
        : 'no';
      return [
        'rec:' + (recSize || 'none'),
        'followed:' + followed,
        'bought:' + (boughtSize || 'none'),
        'avail:' + (availStatus || 'unknown'),
        'fit:' + (build || 'unknown')
      ].join('|');
    }

    function syncCartAttributeFromResult(result, profile) {
      if (!result || !profile) return;
      var value = buildCartAttrValue(result.idealSize, result.availability.status, lastAddedSize, profile.build);
      writeSizeFinderCartAttribute(value);
    }

    var overlay = root.querySelector('[data-size-finder-overlay]');
    var fittingModalEl = root.querySelector('[data-size-finder-modal]');
    var closeBtn = root.querySelector('[data-size-finder-close]');
    var step1 = root.querySelector('[data-size-finder-step="1"]');
    var step2 = root.querySelector('[data-size-finder-step="2"]');
    var step3 = root.querySelector('[data-size-finder-step="3"]');

    var genderGroup = root.querySelector('[data-size-finder-gender]');
    var fitGroup = root.querySelector('[data-size-finder-fit]');
    var heightInput = root.querySelector('[data-size-finder-height]');
    var weightInput = root.querySelector('[data-size-finder-weight]');
    var error1 = root.querySelector('[data-size-finder-error-1]');
    var error2 = root.querySelector('[data-size-finder-error-2]');

    var knowMeasurementsToggle = root.querySelector('[data-size-finder-know-measurements]');
    var buildGroupEl = root.querySelector('[data-size-finder-build-group]');
    var buildGroup = root.querySelector('[data-size-finder-build]');
    var chestGroupEl = root.querySelector('[data-size-finder-chest-group]');
    var chestInput = root.querySelector('[data-size-finder-chest]');
    var underbustGroupEl = root.querySelector('[data-size-finder-underbust-group]');
    var underbustInput = root.querySelector('[data-size-finder-underbust]');

    var nextBtn = root.querySelector('[data-size-finder-next]');
    var backBtn = root.querySelector('[data-size-finder-back]');
    var submitBtn = root.querySelector('[data-size-finder-submit]');
    var editBtn = root.querySelector('[data-size-finder-edit]');
    var doneBtn = root.querySelector('[data-size-finder-done]');

    var resultCardEl = root.querySelector('[data-size-finder-result-card]');
    var resultBadgeEl = root.querySelector('[data-size-finder-result-badge]');
    var resultSizeEl = root.querySelector('[data-size-finder-result-size]');
    var altSizesSectionEl = root.querySelector('[data-size-finder-alt-sizes]');
    var altOptionsEl = root.querySelector('[data-size-finder-alt-options]');
    var altPrevBtn = root.querySelector('[data-size-finder-alt-prev]');
    var altNextBtn = root.querySelector('[data-size-finder-alt-next]');

    var availabilityNoticeEl = root.querySelector('[data-size-finder-availability]');
    var availabilityMessageEl = root.querySelector('[data-size-finder-availability-message]');
    var availabilitySuggestionEl = root.querySelector('[data-size-finder-availability-suggestion]');
    var availabilityCheckBackEl = root.querySelector('[data-size-finder-availability-checkback]');

    var chartOverlay = root.querySelector('[data-size-finder-chart-overlay]');
    var chartCloseBtn = root.querySelector('[data-size-finder-chart-close]');
    var chartTabs = root.querySelector('[data-size-finder-chart-tabs]');
    var chartTableWrap = root.querySelector('[data-size-finder-chart-table]');

    // Plain in-memory flag, deliberately NOT persisted anywhere (not
    // localStorage, not sessionStorage) — the only thing that actually
    // resets on every fresh page load/visit, per request: the passive
    // "Recommended" pill highlight must never be sourced from a saved
    // profile the shopper isn't actively looking at right now, only
    // from a real quiz completion THIS visit (set in the submitBtn
    // handler below). Reopening "Virtual Fitting Room" and landing on
    // the remembered result screen is a separate, intentional
    // convenience (openFitting() below) — that still reads the saved
    // profile — but doesn't set this flag, since the shopper hasn't
    // re-confirmed anything by merely reopening it.
    var completedThisVisit = false;

    var state = { gender: null, fit: null, build: null, knowMeasurements: false };
    var resultState = {
      gender: null,
      chartSizes: null,
      sizeList: [],
      inStockList: [],
      selectedIndex: -1,
      displaySize: null,
      availability: null,
      weight: null,
      method: null,
      estimatedChest: null
    };

    function lockScroll() { document.body.classList.add('size-finder-lock-scroll'); }
    function unlockScroll() { document.body.classList.remove('size-finder-lock-scroll'); }

    function selectPill(group, value) {
      var pills = group.querySelectorAll('.size-finder__pill');
      pills.forEach(function (pill) {
        pill.classList.toggle('is-selected', pill.getAttribute('data-value') === value);
      });
    }

    // Whether Build shows or Chest/Underbust show, and whether Underbust
    // itself shows (women only), all follow from this one function so the
    // toggle and the gender pills can both trigger it without duplicating
    // the visibility rules.
    function syncMeasurementsUI() {
      var knowsMeasurements = !!state.knowMeasurements;
      buildGroupEl.hidden = knowsMeasurements;
      chestGroupEl.hidden = !knowsMeasurements;
      underbustGroupEl.hidden = !knowsMeasurements || state.gender !== 'women';
    }

    genderGroup.addEventListener('click', function (e) {
      var btn = e.target.closest('.size-finder__pill');
      if (!btn) return;
      state.gender = btn.getAttribute('data-value');
      selectPill(genderGroup, state.gender);
      syncMeasurementsUI();
    });

    buildGroup.addEventListener('click', function (e) {
      var btn = e.target.closest('.size-finder__pill');
      if (!btn) return;
      state.build = btn.getAttribute('data-value');
      selectPill(buildGroup, state.build);
    });

    knowMeasurementsToggle.addEventListener('change', function () {
      state.knowMeasurements = knowMeasurementsToggle.checked;
      syncMeasurementsUI();
    });

    fitGroup.addEventListener('click', function (e) {
      var btn = e.target.closest('.size-finder__pill');
      if (!btn) return;
      state.fit = btn.getAttribute('data-value');
      selectPill(fitGroup, state.fit);
    });

    function showStep(step) {
      [step1, step2, step3].forEach(function (el) { el.hidden = el !== step; });
    }

    function openFittingOverlay() {
      chartOverlay.hidden = true;
      overlay.hidden = false;
      lockScroll();
    }

    function closeFittingModal() {
      overlay.hidden = true;
      unlockScroll();
      overlay.style.height = '';
      overlay.style.top = '';
      fittingModalEl.style.maxHeight = '';
    }

    // With no prefill: a blank form (first-time customer). With prefill (the
    // customer's own previously-saved profile): repopulates every field so
    // "Edit measurements" lets them tweak rather than start from scratch.
    function startForm(prefill) {
      error1.hidden = true;
      error2.hidden = true;

      state.gender = prefill ? prefill.gender : null;
      state.fit = prefill ? prefill.fit : null;
      state.build = (prefill && prefill.build) || null;
      state.knowMeasurements = !!(prefill && typeof prefill.chest === 'number');

      heightInput.value = prefill ? prefill.height : '';
      weightInput.value = prefill ? prefill.weight : '';
      chestInput.value = prefill && typeof prefill.chest === 'number' ? prefill.chest : '';
      underbustInput.value = prefill && typeof prefill.underbust === 'number' ? prefill.underbust : '';
      knowMeasurementsToggle.checked = state.knowMeasurements;

      selectPill(genderGroup, state.gender);
      selectPill(fitGroup, state.fit);
      selectPill(buildGroup, state.build);
      syncMeasurementsUI();

      showStep(step1);
      openFittingOverlay();
    }


    // Renders the availability notice (message + optional tighter/looser
    // suggestion + check-back note for sold-out) in place of the standard
    // fit note. Never shown when the true size is actually available.
    function renderAvailabilityNotice(avail) {
      var copy = availabilityMessage(avail);
      if (!copy) {
        availabilityNoticeEl.hidden = true;
        return;
      }

      availabilityMessageEl.textContent = copy.message;

      if (copy.suggestion) {
        availabilitySuggestionEl.textContent = copy.suggestion;
        availabilitySuggestionEl.hidden = false;
      } else {
        availabilitySuggestionEl.hidden = true;
      }

      // No back-in-stock notify mechanism exists on this store today — see
      // requirement 2b's fallback: "otherwise suggest checking back."
      availabilityCheckBackEl.hidden = avail.status !== 'sold-out';

      availabilityNoticeEl.hidden = false;
    }

    function renderAltRow() {
      var avail = resultState.availability;
      // Gap >= 2 (not offered, nothing within one step in either direction):
      // explicitly no alternative to suggest at all, per spec — hide the row
      // entirely rather than show sizes that aren't a real recommendation.
      // Checked via avail.gap, NOT avail.boundarySize — a "sandwiched" size
      // (offered neighbors one step away on BOTH sides, e.g. only S and L
      // offered around a recommended M) also has boundarySize === null, but
      // those neighbors ARE close real options and belong in this row.
      var tooFarForAlternative = avail && avail.status === 'not-offered' && (avail.gap === null || avail.gap >= 2);

      if (tooFarForAlternative || resultState.sizeList.length === 0) {
        altSizesSectionEl.hidden = true;
        return;
      }
      altSizesSectionEl.hidden = false;

      altOptionsEl.innerHTML = '';
      resultState.sizeList.forEach(function (s, i) {
        var inStock = resultState.inStockList.indexOf(s) !== -1;
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'size-finder__alt-pill';
        if (!inStock) btn.classList.add('is-unavailable');
        if (i === resultState.selectedIndex) btn.classList.add('is-selected');
        btn.disabled = !inStock;
        btn.setAttribute('data-index', i);

        var label = document.createElement('span');
        label.textContent = s;
        btn.appendChild(label);

        if (!inStock) {
          var oos = document.createElement('span');
          oos.className = 'size-finder__alt-pill-oos';
          oos.textContent = 'Out of stock';
          btn.appendChild(oos);
        } else if (i === resultState.selectedIndex) {
          var check = document.createElement('span');
          check.className = 'size-finder__alt-pill-check';
          check.innerHTML = '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 10.5 8 14.5 16 6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
          btn.appendChild(check);
        }

        altOptionsEl.appendChild(btn);
      });

      var hasEarlierInStock = false;
      var hasLaterInStock = false;
      resultState.sizeList.forEach(function (s, i) {
        if (resultState.inStockList.indexOf(s) === -1) return;
        if (i < resultState.selectedIndex) hasEarlierInStock = true;
        if (i > resultState.selectedIndex) hasLaterInStock = true;
      });
      altPrevBtn.disabled = !hasEarlierInStock;
      altNextBtn.disabled = !hasLaterInStock;
    }

    // Requirement: never silently substitute the true size. The card
    // always shows resultState.displaySize (the true recommended size, or
    // whatever the customer explicitly picked from the alt row) — it is
    // NOT derived from the alt-row selection, so it renders correctly even
    // when the true size isn't in sizeList at all (not offered).
    function renderResult() {
      var size = resultState.displaySize;
      if (!size) return;

      var avail = resultState.availability;
      var isAvailable = !avail || avail.status === 'available';

      resultSizeEl.textContent = size;
      resultCardEl.classList.toggle('is-unavailable', !isAvailable);
      resultBadgeEl.hidden = !isAvailable;

      if (isAvailable) {
        availabilityNoticeEl.hidden = true;
      } else {
        renderAvailabilityNotice(avail);
      }

      renderAltRow();

      // Never auto-select a variant on the real product page unless the
      // size being shown is genuinely available — the customer either
      // picked it explicitly from the alt row (which only offers in-stock
      // sizes) or it's the true recommendation and it happens to be
      // purchasable. "Not offered" / "sold out" leave the real page alone.
      if (isAvailable) {
        selectVariantSize(config.sectionId, config.optionName, config.optionPosition, size);
      }
    }

    // Explicit customer pick from the alt row: by definition an in-stock,
    // offered size, so it's treated as a fresh "available" display — not
    // a re-evaluation of the true size.
    function selectAltIndex(newIndex) {
      var size = resultState.sizeList[newIndex];
      if (!size || resultState.inStockList.indexOf(size) === -1) return;
      resultState.selectedIndex = newIndex;
      resultState.displaySize = size;
      resultState.availability = { status: 'available', size: size };
      renderResult();
    }

    function showResult(profile, precomputedResult) {
      var result = precomputedResult || recommend(profile, availableSizes, inStockSizes, config.productHandle, parsedChart, config.chartOverride);

      resultState.gender = profile.gender;
      resultState.chartSizes = result.chartSizes;
      resultState.sizeList = orderedAvailableSizes(availableSizes, result.chartSizes);
      resultState.inStockList = (inStockSizes || []).map(function (s) { return String(s).trim().toUpperCase(); });
      resultState.weight = profile.weight;
      resultState.method = result.method;
      resultState.estimatedChest = result.estimatedChest;
      resultState.availability = result.availability;
      resultState.displaySize = result.idealSize;

      var idx = resultState.sizeList.indexOf(result.idealSize);
      resultState.selectedIndex = (result.availability.status === 'available' && idx !== -1) ? idx : -1;

      if (result.availability.status !== 'available') {
        var summary = availabilityEventSummary(config.productHandle, result.availability);
        if (summary !== lastLoggedAvailabilitySummary) {
          logAvailabilityEvent(config.productHandle, result.availability);
          lastLoggedAvailabilitySummary = summary;
        }
      }

      syncCartAttributeFromResult(result, profile);

      renderResult();
      showStep(step3);
      openFittingOverlay();
      syncWidgetState();

      return result;
    }

    closeBtn.addEventListener('click', closeFittingModal);
    doneBtn.addEventListener('click', closeFittingModal);
    editBtn.addEventListener('click', function () {
      startForm(loadProfile());
    });
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) closeFittingModal();
    });

    nextBtn.addEventListener('click', function () {
      var height = parseFloat(heightInput.value);
      var weight = parseFloat(weightInput.value);

      if (!state.gender) {
        error1.textContent = 'Please select a gender.';
        error1.hidden = false;
        return;
      }
      if (!height || height < 100 || height > 230) {
        error1.textContent = 'Please enter a valid height in cm.';
        error1.hidden = false;
        return;
      }
      if (!weight || weight < 30 || weight > 200) {
        error1.textContent = 'Please enter a valid weight in kg.';
        error1.hidden = false;
        return;
      }

      if (state.knowMeasurements) {
        var chest = parseFloat(chestInput.value);
        if (!chest || chest < 60 || chest > 160) {
          error1.textContent = 'Please enter a valid chest measurement in cm.';
          error1.hidden = false;
          return;
        }
      } else if (!state.build) {
        error1.textContent = 'Please select a build.';
        error1.hidden = false;
        return;
      }

      error1.hidden = true;
      showStep(step2);
    });

    backBtn.addEventListener('click', function () {
      showStep(step1);
    });

    submitBtn.addEventListener('click', function () {
      if (!state.fit) {
        error2.textContent = 'Please select a fit preference.';
        error2.hidden = false;
        return;
      }
      error2.hidden = true;

      var profile = {
        gender: state.gender,
        height: parseFloat(heightInput.value),
        weight: parseFloat(weightInput.value),
        fit: state.fit
      };

      if (state.knowMeasurements) {
        profile.chest = parseFloat(chestInput.value);
        if (state.gender === 'women' && underbustInput.value) {
          var underbust = parseFloat(underbustInput.value);
          if (underbust) profile.underbust = underbust;
        }
      } else {
        profile.build = state.build;
      }

      var result = recommend(profile, availableSizes, inStockSizes, config.productHandle, parsedChart, config.chartOverride);
      saveProfile(profile, result.idealSize);
      completedThisVisit = true;
      // showResult() below calls syncWidgetState() itself, which is
      // what actually reads completedThisVisit to (re)apply the pill
      // highlight — no separate call needed here.
      showResult(profile, result);
    });

    altOptionsEl.addEventListener('click', function (e) {
      var btn = e.target.closest('.size-finder__alt-pill');
      if (!btn || btn.disabled) return;
      selectAltIndex(parseInt(btn.getAttribute('data-index'), 10));
    });

    altPrevBtn.addEventListener('click', function () {
      for (var i = resultState.selectedIndex - 1; i >= 0; i--) {
        if (resultState.inStockList.indexOf(resultState.sizeList[i]) !== -1) { selectAltIndex(i); return; }
      }
    });

    altNextBtn.addEventListener('click', function () {
      for (var i = resultState.selectedIndex + 1; i < resultState.sizeList.length; i++) {
        if (resultState.inStockList.indexOf(resultState.sizeList[i]) !== -1) { selectAltIndex(i); return; }
      }
    });

    function renderChartTableRows(chartSizes) {
      if (chartSizes && chartSizes.simple) {
        var simpleHtml = '<table class="size-finder__chart-table"><thead><tr>' +
          '<th>Size</th><th>' + chartSizes.label + '</th>' +
          '</tr></thead><tbody>';

        chartSizes.rows.forEach(function (entry) {
          simpleHtml += '<tr><td>' + entry.size + '</td><td>' + entry.value + '</td></tr>';
        });

        simpleHtml += '</tbody></table>';
        chartTableWrap.innerHTML = simpleHtml;
        return;
      }

      var html = '<table class="size-finder__chart-table"><thead><tr>' +
        '<th>Size</th><th>Height (cm)</th><th>Weight (kg)</th>' +
        '</tr></thead><tbody>';

      chartSizes.forEach(function (entry) {
        html += '<tr><td>' + entry.size + '</td>' +
          '<td>' + entry.height[0] + '–' + entry.height[1] + '</td>' +
          '<td>' + entry.weight[0] + '–' + entry.weight[1] + '</td></tr>';
      });

      html += '</tbody></table>';
      chartTableWrap.innerHTML = html;
    }

    // The Size Chart modal always prefers this product's own parsed guide
    // (Tier 0) — a single table, no Women/Men tabs, since there's only
    // ever one real table per product and it's already correct for
    // whichever sex that product is for. Tabs only reappear when parsing
    // failed and we've fallen back to the generic per-sex charts, exactly
    // as before the auto-parser existed.
    function renderChartTable(fallbackGender) {
      if (parsedChart) {
        chartTabs.hidden = true;
        renderChartTableRows(parsedChart);
        return;
      }

      chartTabs.hidden = false;
      chartTabs.querySelectorAll('.size-finder__chart-tab').forEach(function (tab) {
        tab.classList.toggle('is-active', tab.getAttribute('data-value') === fallbackGender);
      });
      var fallbackChart = fallbackGender === 'women' ? CHARTS.WOMEN_STANDARD.sizes : resolveChart(config.productHandle, 'men').sizes;
      renderChartTableRows(fallbackChart);
    }

    chartTabs.addEventListener('click', function (e) {
      var btn = e.target.closest('.size-finder__chart-tab');
      if (!btn) return;
      renderChartTable(btn.getAttribute('data-value'));
    });

    function openChartOverlay() {
      overlay.hidden = true;
      var profile = loadProfile();
      var defaultGender = (profile && profile.gender) || 'women';
      renderChartTable(defaultGender);
      chartOverlay.hidden = false;
      lockScroll();
    }

    function closeChartModal() {
      chartOverlay.hidden = true;
      unlockScroll();
    }

    chartCloseBtn.addEventListener('click', closeChartModal);
    chartOverlay.addEventListener('click', function (e) {
      if (e.target === chartOverlay) closeChartModal();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (!overlay.hidden) closeFittingModal();
      if (!chartOverlay.hidden) closeChartModal();
    });

    // Public API used by the delegated trigger-click handler below. If a
    // profile is already saved (and not expired), clicking the trigger goes
    // straight to the result screen, which has its own "Edit measurements"
    // button (back into startForm(prefill)) rather than a blank form.
    root.__sizeFinderApi = {
      openFitting: function () {
        var profile = loadProfile();
        if (profile) {
          showResult(profile);
        } else {
          startForm();
        }
      },
      openChart: openChartOverlay,
      // Passive, on-page "Recommended" pill highlight — deliberately
      // gated on completedThisVisit, not just loadProfile(): a saved
      // profile can be up to 30 days old (see loadProfile's own TTL
      // comment), and per request this highlight must never appear
      // from that alone. Reopening "Virtual Fitting Room" and landing
      // on a remembered result (openFitting() above) still works, and
      // still shows the real answer inside that modal — it just
      // doesn't, by itself, light up the pill on the page behind it.
      getDisplaySize: function () {
        if (!completedThisVisit) return null;
        var profile = loadProfile();
        if (!profile) return null;
        // Always the true size — the persisted banner never substitutes
        // an available alternative for the real recommendation either.
        return recommend(profile, availableSizes, inStockSizes, config.productHandle, parsedChart, config.chartOverride).idealSize;
      },
      // Called from the delegated add-to-cart submit listener below. Only
      // customers who've actually used the Size Finder (have a saved
      // profile) get a cart attribute — no profile means nothing to compare
      // "bought" against, so there's nothing worth logging.
      recordAddToCart: function () {
        var profile = loadProfile();
        if (!profile) return;
        var radioName = (config.optionName && config.optionPosition)
          ? (config.optionName + '-' + config.optionPosition)
          : null;
        var checked = radioName
          ? document.querySelector('input[name="' + window.CSS.escape(radioName) + '"]:checked')
          : null;
        lastAddedSize = checked ? checked.value : null;
        var result = recommend(profile, availableSizes, inStockSizes, config.productHandle, parsedChart, config.chartOverride);
        syncCartAttributeFromResult(result, profile);
      }
    };

    syncWidgetState();
  }

  function findModalApi(instanceId) {
    if (!instanceId) return null;
    var modalRoot = document.querySelector(
      '[data-size-finder][data-instance-id="' + window.CSS.escape(instanceId) + '"]'
    );
    return modalRoot && modalRoot.__sizeFinderApi ? modalRoot.__sizeFinderApi : null;
  }

  // The recommendation box sits inside <variant-picker> (so it lands right
  // under the real Size buttons), so — unlike the modal — it can be
  // replaced wholesale by the theme's own AJAX re-render. It has no
  // listeners of its own (just text), so re-syncing it from scratch after
  // any such replace is enough. The "Virtual Fitting Room" trigger always
  // stays visible — clicking it goes to the form (first time) or straight
  // to the result screen with an Edit measurements option (returning).
  function syncWidgetState() {
    document.querySelectorAll('[data-size-finder-reco]').forEach(function (box) {
      var instanceId = box.getAttribute('data-instance-id');
      var api = findModalApi(instanceId);
      var size = api ? api.getDisplaySize() : null;

      // The recommendation now shows as a highlight directly on the
      // matching Size pill/dropdown-item (not just this sr-only text
      // box) — per request, one visual element rather than two that
      // can disagree. Scoped to this box's own <variant-picker> so
      // multiple product instances on a page (e.g. quick-view) never
      // cross-highlight each other.
      var picker = box.closest('variant-picker');
      if (picker) {
        var optionEls = picker.querySelectorAll('[data-size-option-pill]');
        for (var i = 0; i < optionEls.length; i++) {
          optionEls[i].classList.remove('is-recommended-size');
        }
        if (size) {
          for (var j = 0; j < optionEls.length; j++) {
            var elValue = (optionEls[j].getAttribute('data-value') || '').trim().toLowerCase();
            if (elValue === String(size).trim().toLowerCase()) {
              optionEls[j].classList.add('is-recommended-size');
              break;
            }
          }
        }
      }

      if (!size) {
        box.hidden = true;
        return;
      }

      var sizeEl = box.querySelector('[data-size-finder-reco-size]');
      if (sizeEl) sizeEl.textContent = size;
      box.hidden = false;
    });
  }

  function watchForVariantPickerReplacement() {
    var pending = false;
    var observer = new MutationObserver(function () {
      if (pending) return;
      pending = true;
      setTimeout(function () {
        pending = false;
        // Picks up any [data-size-finder] root added after the initial
        // page-load scan (e.g. apparel-bundle-size-links.js injecting a
        // modal for a dynamically-selected bundle component) — same
        // initModal() the initial querySelectorAll pass uses, just applied
        // to whatever's new. __sizeFinderApi is the "already done" marker
        // initModal itself sets, so this is safe to run on every mutation.
        document.querySelectorAll('[data-size-finder]').forEach(function (root) {
          if (!root.__sizeFinderApi) initModal(root);
        });
        syncWidgetState();
      }, 150);
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function initDelegatedTriggers() {
    document.addEventListener('click', function (e) {
      var fittingBtn = e.target.closest('[data-size-finder-trigger]');
      var chartBtn = e.target.closest('[data-size-finder-chart-trigger]');
      var btn = fittingBtn || chartBtn;
      if (!btn) return;

      var group = btn.closest('[data-size-finder-triggers]');
      var instanceId = group ? group.getAttribute('data-instance-id') : null;
      var api = findModalApi(instanceId);
      if (!api) return;

      if (fittingBtn) api.openFitting();
      else api.openChart();
    });
  }

  // Fires on every add-to-cart submission on the page (delegated, so it
  // survives the theme's own AJAX re-renders of the product form). Purely
  // observational — never preventDefault/stopPropagation, never delays or
  // blocks the real add-to-cart request. The cart-attribute write it
  // triggers is a separate, best-effort fetch to /cart/update.js.
  function initCartAttributeSync() {
    document.addEventListener('submit', function (e) {
      var form = e.target.closest('[data-type="add-to-cart-form"]');
      if (!form) return;
      var productFormEl = form.closest('product-form');
      var instanceId = productFormEl && productFormEl.getAttribute('data-instance-id');
      var api = findModalApi(instanceId);
      if (api && api.recordAddToCart) api.recordAddToCart();
    }, true);
  }

  function init() {
    document.querySelectorAll('[data-size-finder]').forEach(initModal);
    initDelegatedTriggers();
    initCartAttributeSync();
    watchForVariantPickerReplacement();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
